import { createEffect, createEvent, createStore } from 'effector';
import request from '@helpers/request';
import api from '@constants/endpoints';
import { AxiosResponse } from 'axios';

import spamStore, { getList as getSpamList, getById as getSpamById } from './spam';
import spamCommentsStore, {
  getList as getSpamCommentsList,
  getById as getSpamCommentsById,
} from './spamComments';
import spamUsersStore, { getList as getSpamUsersList } from './spamUsers/spamUsers';
import spamStatusesStore, { getList as getSpamStatusesList } from './spamStatuses';

import { block as blockPost, unblock as unblockPost, hidePost, unhidePost } from './posts';
import { block as blockComment, unblock as unblockComment } from './comments';
import { unblockUser } from './users';

type SpamsUnreadState = {
  spam: boolean;
  spamComments: boolean;
  spamUsers: boolean;
  spamStatuses: boolean;
};

type SpamUnreadKey = keyof SpamsUnreadState;

const EMPTY_UNREAD_STATE: SpamsUnreadState = {
  spam: false,
  spamComments: false,
  spamUsers: false,
  spamStatuses: false,
};

const setUnreadSpam = createEvent<{ listName: SpamUnreadKey; value: boolean }>('setUnreadSpam');

let hasBootstrappedUnread = false;

const unreadRequests: Array<{
  listName: SpamUnreadKey;
  request: () => Promise<AxiosResponse>;
  hasItems: (response: AxiosResponse) => boolean;
}> = [
  {
    listName: 'spam',
    request: () =>
      request(
        api.spam.getList,
        {
          params: {
            paged: true,
            size: 1,
            page: 0,
            needChecked: false,
          },
        },
        { catch: false },
      ),
    hasItems: (response) => Boolean(response?.data?.body?.content?.length),
  },
  {
    listName: 'spamComments',
    request: () =>
      request(
        api.spam.getComments,
        {
          params: {
            paged: true,
            size: 1,
            page: 0,
            needChecked: false,
          },
        },
        { catch: false },
      ),
    hasItems: (response) => Boolean(response?.data?.body?.content?.length),
  },
  {
    listName: 'spamUsers',
    request: () =>
      request(
        api.spam.getUsers,
        {
          params: {
            paged: true,
            size: 1,
            page: 0,
            checkedType: 'UNCHECKED',
            showBlocked: false,
            hideUnblocked: false,
          },
        },
        { catch: false },
      ),
    hasItems: (response) => Boolean(response?.data?.body?.content?.length),
  },
  {
    listName: 'spamStatuses',
    request: () =>
      request(
        api.spam.getStatuses,
        {
          data: {
            locale: undefined,
            pagination: {
              type: 'PAGE',
              pageSize: 1,
              page: 0,
            },
            nickname: '',
            statusAuthorId: '',
          },
        },
        { catch: false },
      ),
    hasItems: (response) => Boolean(response?.data?.body?.claimedStatuses?.page?.length),
  },
];

export const getUnreadSpams = createEffect({
  name: 'getUnreadSpams',
  async handler() {
    return await Promise.allSettled(unreadRequests.map(({ request }) => request()));
  },
});

export function bootstrapUnreadSpams() {
  if (hasBootstrappedUnread) {
    return;
  }

  hasBootstrappedUnread = true;
  getUnreadSpams();
}

function handleGetUnreadSpams(
  state: SpamsUnreadState,
  responses: Array<PromiseSettledResult<AxiosResponse>>,
): SpamsUnreadState {
  const nextState: SpamsUnreadState = { ...EMPTY_UNREAD_STATE };

  responses.forEach((response, index) => {
    const requestConfig = unreadRequests[index];

    if (!requestConfig) {
      return;
    }

    if (response.status === 'fulfilled') {
      nextState[requestConfig.listName] = requestConfig.hasItems(response.value);
    }
  });

  return nextState;
}

function handleSetUnreadSpam(
  state: SpamsUnreadState,
  payload: { listName: SpamUnreadKey; value: boolean },
): SpamsUnreadState {
  const { listName, value } = payload;

  return {
    ...state,
    [listName]: value,
  };
}

const spamsUnread = createStore<SpamsUnreadState>(EMPTY_UNREAD_STATE)
  .on(getUnreadSpams.doneData, handleGetUnreadSpams)
  .on(setUnreadSpam, handleSetUnreadSpam);

export default spamsUnread;

/**
 * Helpers
 */

const setSpamUnread = (value: boolean) => {
  setUnreadSpam({ listName: 'spam', value });
};

const setSpamCommentsUnread = (value: boolean) => {
  setUnreadSpam({ listName: 'spamComments', value });
};

const setSpamUsersUnread = (value: boolean) => {
  setUnreadSpam({ listName: 'spamUsers', value });
};

const setSpamStatusesUnread = (value: boolean) => {
  setUnreadSpam({ listName: 'spamStatuses', value });
};

const canUpdateSpamUnread = () => {
  const { filters } = spamStore.getState();
  return !filters.needChecked;
};

const canUpdateSpamCommentsUnread = () => {
  const { filters } = spamCommentsStore.getState();
  return !filters.needChecked;
};

const canUpdateSpamUsersUnread = () => {
  const { filters } = spamUsersStore.getState();

  return filters.checkedType === 'UNCHECKED' && !filters.showBlocked && !filters.hideUnblocked;
};

const canUpdateSpamStatusesUnread = () => {
  const { filters } = spamStatusesStore.getState();

  return !filters.nickname && !filters.statusAuthorId;
};

getSpamList.done.watch(({ result }) => {
  if (!canUpdateSpamUnread()) {
    return;
  }

  setSpamUnread(Boolean(result?.data?.body?.content?.length));
});

getSpamById.done.watch(({ result }) => {
  if (!canUpdateSpamUnread()) {
    return;
  }

  setSpamUnread(Boolean(result?.data?.body?.content?.length));
});

getSpamCommentsList.done.watch(({ result }) => {
  if (!canUpdateSpamCommentsUnread()) {
    return;
  }

  setSpamCommentsUnread(Boolean(result?.data?.body?.content?.length));
});

getSpamCommentsById.done.watch(({ result }) => {
  if (!canUpdateSpamCommentsUnread()) {
    return;
  }

  setSpamCommentsUnread(Boolean(result?.data?.body?.content?.length));
});

getSpamUsersList.done.watch(({ result }) => {
  if (!canUpdateSpamUsersUnread()) {
    return;
  }

  setSpamUsersUnread(Boolean(result?.data?.body?.content?.length));
});

getSpamStatusesList.done.watch(({ result }) => {
  if (!canUpdateSpamStatusesUnread()) {
    return;
  }

  setSpamStatusesUnread(Boolean(result?.data?.body?.claimedStatuses?.page?.length));
});

blockPost.done.watch(() => {
  if (!canUpdateSpamUnread()) {
    return;
  }

  setSpamUnread(spamStore.getState().list.length > 0);
});

unblockPost.done.watch(() => {
  if (!canUpdateSpamUnread()) {
    return;
  }

  setSpamUnread(spamStore.getState().list.length > 0);
});

hidePost.done.watch(() => {
  if (!canUpdateSpamUnread()) {
    return;
  }

  setSpamUnread(spamStore.getState().list.length > 0);
});

unhidePost.done.watch(() => {
  if (!canUpdateSpamUnread()) {
    return;
  }

  setSpamUnread(spamStore.getState().list.length > 0);
});

blockComment.done.watch(() => {
  if (!canUpdateSpamCommentsUnread()) {
    return;
  }

  setSpamCommentsUnread(spamCommentsStore.getState().list.length > 0);
});

unblockComment.done.watch(() => {
  if (!canUpdateSpamCommentsUnread()) {
    return;
  }

  setSpamCommentsUnread(spamCommentsStore.getState().list.length > 0);
});

unblockUser.done.watch(() => {
  if (!canUpdateSpamUsersUnread()) {
    return;
  }

  setSpamUsersUnread(spamUsersStore.getState().list.length > 0);
});
